import { useCallback, useEffect, useMemo, useState } from 'react';
import { ErroApi } from '../lib/api';
import { useAutenticacao } from '../lib/auth';
import {
  buscarEscalaCultos,
  meioDoDia,
  sincronizarCultos,
  somarDias,
  type RespostaCultos,
} from '../lib/escalaCultos';
import { diaDaSemana, mesCurto, numeroDoDia, quandoEmPalavras } from '../lib/datas';
import { gradeDoMes, HOJE_SP, mesAtual, nomeDoMes, somarMeses } from '../lib/agenda';
import { Alerta, Botao, Cartao, Modal, SeletorBusca } from '../componentes/Interface';
import { buscarMinisterios, type Ministerio } from '../lib/escalas';
import Escalas from './Escalas';
import { useConfirmacao } from '../componentes/Confirmacao';
import CartaoDiaCulto from '../componentes/CartaoDiaCulto';
import Layout from '../componentes/Layout';

const SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const FILTROS = [
  { valor: 'todos', rotulo: 'Todos' },
  { valor: 'fe', rotulo: 'Culto da Fé' },
  { valor: 'arena', rotulo: 'Arena' },
  { valor: 'domingo', rotulo: 'Domingo' },
] as const;
/** a lista comeca com oito semanas e cresce de quatro em quatro */
const JANELA_INICIAL = 56;
/** opcao do seletor que mostra as equipes de cada demanda (a escala dos cultos de sempre) */
const EQUIPES = '__equipes';
const OPCAO_EQUIPES = { valor: EQUIPES, rotulo: 'Equipes dos cultos' };

/**
 * Escala dos cultos: Culto da Fé (terça), Arena (sábado) e Domingo (10h e 19h,
 * cada um com a sua escala). Em cada um, qual equipe cuida de cada demanda. O admin monta; o 12
 * e o ADM das equipes escaladas confirmam. Dá para ver em lista ou em agenda.
 */
export default function EscalaCultos() {
  const { usuario } = useAutenticacao();
  const [visao, setVisao] = useState<'lista' | 'agenda'>('lista');
  const [quando, setQuando] = useState<'proximos' | 'anteriores'>('proximos');
  const [filtro, setFiltro] = useState<string>('todos');
  const [janela, setJanela] = useState(JANELA_INICIAL);
  const [mes, setMes] = useState(mesAtual());

  const [dados, setDados] = useState<RespostaCultos | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [carregado, setCarregado] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [diaAberto, setDiaAberto] = useState<string | null>(null);

  const { perguntar, dialogo } = useConfirmacao();
  const hoje = HOJE_SP();

  // seletor de cima: equipes dos cultos ou a escala de um ministerio (Louvor, Midia...)
  const [fonte, setFonte] = useState(EQUIPES);
  const [ministerios, setMinisterios] = useState<Ministerio[]>([]);
  useEffect(() => {
    buscarMinisterios()
      .then((r) => setMinisterios(r.ministerios))
      .catch(() => setMinisterios([]));
  }, []);

  // o periodo pedido depende da visao: lista (proximos/anteriores) ou o mes da agenda
  const periodo = useMemo(() => {
    if (visao === 'agenda') {
      const grade = gradeDoMes(mes);
      return { de: grade[0]!.dia, ate: grade[grade.length - 1]!.dia };
    }
    if (quando === 'anteriores') return { de: somarDias(hoje, -84), ate: somarDias(hoje, -1) };
    return { de: hoje, ate: somarDias(hoje, janela) };
  }, [visao, mes, quando, janela, hoje]);

  const carregar = useCallback(async () => {
    try {
      const d = await buscarEscalaCultos(periodo.de, periodo.ate, filtro);
      setDados(d);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar a escala dos cultos.');
    } finally {
      setCarregado(true);
    }
  }, [periodo, filtro]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const podeEditar = dados?.podeEditar ?? false;
  const doze = usuario?.papel === 'lider' || usuario?.papel === 'lider_adm';
  const meuMembroId = dados?.meuMembroId ?? null;

  const aoErrar = (mensagem: string) => {
    setErro(mensagem);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  async function atualizarNomes() {
    setSincronizando(true);
    try {
      const r = await sincronizarCultos();
      setAviso(
        r.entraram || r.sairam
          ? `Nomes atualizados: ${r.entraram} entraram e ${r.sairam} saíram.`
          : 'Tudo em dia: os nomes já batem com Equipes.',
      );
      await carregar();
    } catch (e) {
      aoErrar(e instanceof ErroApi ? e.message : 'Não consegui atualizar os nomes.');
    } finally {
      setSincronizando(false);
    }
  }

  const dias = dados?.dias ?? [];
  const souEu = (d: (typeof dias)[number]) =>
    meuMembroId !== null && d.cultos.some((c) => c.escalados.some((p) => p.membroId === meuMembroId));

  // na lista, quem so olha ve os dias com escala; o admin ve todos para montar
  const naLista = useMemo(() => {
    const base = podeEditar && quando === 'proximos' ? dias : dias.filter((d) => d.demandas.length > 0);
    return quando === 'anteriores' ? [...base].reverse() : base;
  }, [dias, podeEditar, quando]);

  const porDia = useMemo(() => new Map(dias.map((d) => [d.dia, d])), [dias]);
  const aberto = diaAberto ? porDia.get(diaAberto) ?? null : null;
  const semContaUnicos = [...new Map((dados?.semConta ?? []).map((p) => [p.membroId, p])).values()];

  const cartao = (d: (typeof dias)[number], semMoldura = false) => (
    <CartaoDiaCulto
      key={d.dia}
      dia={d}
      demandas={dados?.demandas ?? []}
      turnos={dados?.turnos ?? []}
      equipes={dados?.equipes ?? []}
      podeEditar={podeEditar}
      meuMembroId={meuMembroId}
      somenteLeitura={d.dia < hoje}
      perguntar={perguntar}
      aoMudar={carregar}
      aoErrar={aoErrar}
      aoAvisar={(m) => {
        setErro(null);
        setAviso(m);
      }}
      semMoldura={semMoldura}
    />
  );

  if (fonte !== EQUIPES) {
    return (
      <Layout titulo="Escala dos cultos" subtitulo="Quem serve em cada culto, por ministério, com confirmação de presença.">
        <Escalas
          key={fonte}
          embutido={{ chaveInicial: fonte, opcaoVoltar: OPCAO_EQUIPES, aoVoltar: () => setFonte(EQUIPES) }}
        />
      </Layout>
    );
  }

  return (
    <Layout
      titulo="Escala dos cultos"
      subtitulo={
        podeEditar
          ? 'Culto da Fé, Arena e Domingo (10h e 19h separados): escolha a equipe de cada demanda. O 12 e o ADM de cada equipe entram sozinhos e confirmam.'
          : doze
            ? 'Qual equipe cuida de cada demanda nos cultos. Quando for a sua, confirme sua presença.'
            : 'Qual equipe cuida de cada demanda nos cultos.'
      }
      acoes={
        podeEditar && (
          <Botao variante="secundario" carregando={sincronizando} onClick={() => void atualizarNomes()}>
            Atualizar nomes
          </Botao>
        )
      }
    >
      {erro && (
        <div className="mb-4 max-w-3xl">
          <Alerta>{erro}</Alerta>
        </div>
      )}
      {aviso && (
        <div className="mb-4 max-w-3xl">
          <Alerta tipo="ok">{aviso}</Alerta>
        </div>
      )}

      {podeEditar && semContaUnicos.length > 0 && (
        <div className="mb-4 max-w-3xl rounded-2xl bg-amber-50 p-4 ring-1 ring-amber-200">
          <p className="text-sm font-semibold text-amber-900">
            {semContaUnicos.length === 1
              ? '1 pessoa das equipes escaladas está sem conta'
              : `${semContaUnicos.length} pessoas das equipes escaladas estão sem conta`}
          </p>
          <p className="mt-1 text-xs text-amber-800">
            Elas não entram na escala e não ficam sabendo. Crie a conta em Equipe e toque em “Atualizar nomes”.
          </p>
          <ul className="mt-2 flex flex-wrap gap-2 text-sm text-tinta-900">
            {semContaUnicos.map((p) => (
              <li key={p.membroId} className="rounded-lg bg-white px-2 py-0.5 ring-1 ring-amber-200">
                {p.nome}{' '}
                <span className="text-xs text-tinta-400">
                  · {p.papel === 'doze' ? '12 da Igreja' : 'ADM'} · {p.equipes.join(' / ')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {ministerios.length > 0 && (
        <div className="mb-4 flex max-w-3xl">
          <SeletorBusca
            className="min-w-0 max-w-xs flex-1"
            rotulo="Ministério"
            opcoes={[OPCAO_EQUIPES, ...ministerios.map((m) => ({ valor: m.chave, rotulo: m.nome }))]}
            valor={fonte}
            aoEscolher={setFonte}
            vazio="Nenhum ministério com esse nome."
          />
        </div>
      )}

      <div className="mb-4 flex max-w-3xl flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1 rounded-xl bg-white p-1 shadow-cartao">
          {FILTROS.map((f) => (
            <button
              key={f.valor}
              onClick={() => setFiltro(f.valor)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                filtro === f.valor ? 'bg-tinta-900 text-white' : 'text-tinta-600 hover:bg-tinta-100'
              }`}
            >
              {f.rotulo}
            </button>
          ))}
        </div>
        <div className="ml-auto flex shrink-0 rounded-xl bg-white p-1 shadow-cartao">
          {(['lista', 'agenda'] as const).map((v) => (
            <button
              key={v}
              onClick={() => setVisao(v)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                visao === v ? 'bg-tinta-900 text-white' : 'text-tinta-600 hover:bg-tinta-100'
              }`}
            >
              {v === 'lista' ? 'Lista' : 'Agenda'}
            </button>
          ))}
        </div>
      </div>

      {visao === 'agenda' && (
        <Cartao className="max-w-5xl">
          <div className="mb-4 flex items-center justify-between">
            <button onClick={() => setMes(somarMeses(mes, -1))} className="rounded-lg px-3 py-1 text-tinta-400 hover:bg-tinta-100">
              &larr;
            </button>
            <h3 className="font-semibold text-tinta-900">{nomeDoMes(mes)}</h3>
            <button onClick={() => setMes(somarMeses(mes, 1))} className="rounded-lg px-3 py-1 text-tinta-400 hover:bg-tinta-100">
              &rarr;
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold uppercase text-tinta-400">
            {SEMANA.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-1">
            {gradeDoMes(mes).map(({ dia, doMes }) => {
              const d = porDia.get(dia);
              const daVez = d?.demandas.find((x) => x.demanda === 'equipe')?.equipes ?? [];
              const outras = d ? d.demandas.filter((x) => x.demanda !== 'equipe').length : 0;
              const meu = d ? souEu(d) : false;
              return (
                <button
                  key={dia}
                  disabled={!d}
                  onClick={() => d && setDiaAberto(dia)}
                  className={`min-h-[5.5rem] rounded-lg border p-1 text-left transition ${
                    meu ? 'border-tinta-900' : 'border-transparent'
                  } ${d ? 'bg-tinta-100/60 hover:border-tinta-400' : ''} ${doMes ? '' : 'opacity-40'}`}
                >
                  <span
                    className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                      dia === hoje ? 'bg-tinta-900 font-bold text-white' : 'text-tinta-600'
                    }`}
                  >
                    {Number(dia.slice(8))}
                  </span>
                  {d && (
                    <span className="mt-0.5 block space-y-0.5">
                      <span className="block truncate text-[10px] font-bold uppercase tracking-wide text-tinta-900">
                        {d.culto.nome.replace('Culto de ', '').replace('Culto da ', '')}
                      </span>
                      {daVez.length > 0 && (
                        <span className="block truncate rounded bg-tinta-900 px-1 text-[10px] text-white">
                          {daVez.map((e) => e.nome).join(' / ')}
                        </span>
                      )}
                      {outras > 0 && (
                        <span className="block truncate text-[10px] text-tinta-600">
                          +{outras} {outras === 1 ? 'demanda' : 'demandas'}
                        </span>
                      )}
                      {d.demandas.length === 0 && podeEditar && dia >= hoje && (
                        <span className="block truncate text-[10px] text-amber-700">sem escala</span>
                      )}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-tinta-400">
            Toque num dia de culto para ver{podeEditar ? ' e montar' : ''} a escala. A faixa preta é a equipe da vez
            {meuMembroId !== null ? '; a borda marca os cultos em que você está' : ''}.
          </p>
        </Cartao>
      )}

      {visao === 'lista' && (
        <>
          <div className="mb-4 flex max-w-md rounded-2xl bg-white p-1 shadow-cartao">
            {(
              [
                { valor: 'proximos', rotulo: 'Próximos' },
                { valor: 'anteriores', rotulo: 'Anteriores' },
              ] as const
            ).map((a) => (
              <button
                key={a.valor}
                onClick={() => setQuando(a.valor)}
                className={`flex-1 rounded-xl px-4 py-2 text-sm font-semibold transition ${
                  quando === a.valor ? 'bg-tinta-900 text-white' : 'text-tinta-400 hover:bg-tinta-100'
                }`}
              >
                {a.rotulo}
              </button>
            ))}
          </div>

          {carregado && naLista.length === 0 && (
            <Cartao className="max-w-3xl">
              <p className="text-sm text-tinta-400">
                {quando === 'anteriores' ? 'Nenhum culto anterior com escala.' : 'A escala dos próximos cultos ainda não foi montada.'}
              </p>
            </Cartao>
          )}

          <div className="max-w-3xl space-y-6">
            {naLista.map((d) => {
              const iso = meioDoDia(d.dia);
              return (
                <section key={d.dia}>
                  <div className="mb-2 flex items-baseline justify-between gap-3 px-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-tinta-900">{numeroDoDia(iso)}</span>
                      <span className="text-xs font-semibold uppercase tracking-wide text-tinta-400">{mesCurto(iso)}</span>
                      <span className="text-sm text-tinta-400">· {diaDaSemana(iso)}</span>
                    </div>
                    <span className="text-sm text-tinta-400">{quandoEmPalavras(iso)}</span>
                  </div>
                  <div className={souEu(d) ? 'rounded-2xl ring-2 ring-tinta-900' : ''}>{cartao(d)}</div>
                </section>
              );
            })}
          </div>

          {quando === 'proximos' && carregado && (
            <div className="mt-6 max-w-3xl">
              <Botao variante="secundario" onClick={() => setJanela((j) => j + 28)}>
                Ver mais 4 semanas
              </Botao>
            </div>
          )}
        </>
      )}

      {aberto && (
        <Modal
          titulo={`${diaDaSemana(meioDoDia(aberto.dia))}, ${numeroDoDia(meioDoDia(aberto.dia))} ${mesCurto(meioDoDia(aberto.dia))}`}
          aoFechar={() => setDiaAberto(null)}
        >
          <div className="mt-3">{cartao(aberto, true)}</div>
          <div className="mt-4 flex justify-end">
            <Botao variante="secundario" onClick={() => setDiaAberto(null)}>
              Fechar
            </Botao>
          </div>
        </Modal>
      )}

      {dialogo}
    </Layout>
  );
}
